import queryString from "query-string";
import React, {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import * as models from "liberty-core/api/models";
import {me, login} from "liberty-core/api/user";

import {setAuthData} from "lib/authStorage";
import {
  clearCurrentApiVersion,
  clearRemoteFeatureFlags,
  refreshRemoteFeatureFlags,
  setCurrentApiVersion,
} from "lib/remoteFeatureFlags";
import {saveSessionStorage} from "lib/storage";
import {CURRENT_PATIENT_ID_KEY} from "lib/useCurrentPatientId";

export type UserData = {
  name: string;
} & models.FullUser;

export type AuthenticatedUser = {
  id: number;
  username: string;
  data: UserData;
};

const __AUTH = {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars,@typescript-eslint/no-empty-function
  _setAuthenticatedUser: (user: AuthenticatedUser | null) => {},
};

export const ERR_CODE_NOT_AUTHORIZED_EXCEPTION = "NotAuthorizedException";
export const ERR_CODE_APP_OUTDATED = "AppOutdatedException";

export class AppOutdatedError extends Error {
  code = ERR_CODE_APP_OUTDATED;
  constructor(message = "This version of the app is no longer supported.") {
    super(message);
    this.name = "AppOutdatedError";
  }
}

async function enforceVersionCutoff(): Promise<void> {
  const result = await refreshRemoteFeatureFlags().catch(() => null);
  if (result && result.status === "below-cutoff") {
    __AUTH._setAuthenticatedUser(null);
    setAuthData(undefined);
    await clearRemoteFeatureFlags();
    clearCurrentApiVersion();
    throw new AppOutdatedError();
  }
}

export function getAuthenticatedUser(): Promise<AuthenticatedUser | null> {
  return me()
    .then((user) => {
      setCurrentApiVersion(user.apiVersion);
      const userObj: AuthenticatedUser = {
        id: user.id,
        username: user.email,
        data: {
          ...user,
          name: `${user.firstName} ${user.lastName}`,
        },
      };
      __AUTH._setAuthenticatedUser(userObj);
      return userObj;
    })
    .catch((err) => {
      throw err;
    });
}

const DOMAIN = process.env.REACT_APP_IS_QA === "true" ? "test.rx365" : "rx365";
export function signIn(
  email: string,
  password: string,
): Promise<{
  user: AuthenticatedUser;
  result: "SUCCESS";
} | null> {
  return login({email, password})
    .then((user) => setAuthData({accessToken: user.token}))
    .then(me)
    .then(async (user) => {
      setCurrentApiVersion(user.apiVersion);
      const subdomain = window.location.host.split(".")[0];
      if (
        user.pharmacy.subdomain &&
        user.pharmacy.subdomain.toLowerCase() !== subdomain.toLowerCase()
      ) {
        setAuthData(undefined);
        __AUTH._setAuthenticatedUser(null);
        window.location.replace(
          `https://${
            user.pharmacy.subdomain
          }.${DOMAIN}.com/login?${queryString.stringify({mismatched: true})}`,
        );
        return null;
      }

      const userObj: AuthenticatedUser = {
        id: user.id,
        username: user.email,
        data: {
          ...user,
          name: `${user.firstName} ${user.lastName}`,
        },
      };
      __AUTH._setAuthenticatedUser(userObj);

      // Fetch per-store feature flags from the mobile gateway. If the API
      // server version is below the file's cutoff, refuse sign-in and force
      // the user to update. Network failures and soft-warnings let sign-in
      // proceed.
      await enforceVersionCutoff();

      return {
        user: userObj,
        result: "SUCCESS" as const,
      };
    });
}

export async function signOut() {
  setAuthData(undefined);
  __AUTH._setAuthenticatedUser(null);
  saveSessionStorage(CURRENT_PATIENT_ID_KEY, undefined);
  await clearRemoteFeatureFlags();
  clearCurrentApiVersion();
}

function _useAuthenticatedUserHook(
  initialUser: AuthenticatedUser | null,
): [AuthenticatedUser | null, boolean, (userData: Partial<UserData>) => void] {
  const [user, setUser] = useState<AuthenticatedUser | null>(initialUser);
  const [loading, setLoading] = useState(!user);

  useEffect(() => {
    __AUTH._setAuthenticatedUser = (authedUser: AuthenticatedUser | null) => {
      setUser((prevAuthedUser) => {
        if (prevAuthedUser && authedUser) {
          return {
            ...authedUser,
            data: {...prevAuthedUser.data, ...authedUser.data},
          };
        }
        return authedUser;
      });
    };

    getAuthenticatedUser()
      .then((authedUser) => {
        setUser(authedUser);
        setLoading(false);
        if (authedUser) {
          // Refresh per-store feature flags when an existing web session is
          // restored, enforcing the version cutoff. If the app is below
          // the cutoff, sign the user out and let the caller render the
          // outdated-version screen.
          enforceVersionCutoff().catch((err) => {
            // eslint-disable-next-line no-console
            console.error("Outdated app detected on session restore", err);
            setUser(null);
          });
        }
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("Error getting currentAuthenticatedUser", err);
        setLoading(false);
      });
  }, []);

  const setUserData = useCallback((userData: Partial<UserData>) => {
    setUser((prevUser) => {
      if (!prevUser) {
        return prevUser;
      }
      return {
        ...prevUser,
        data: {
          ...prevUser.data,
          ...userData,
        },
      };
    });
  }, []);

  return [user, loading, setUserData];
}

const AuthContext = createContext<
  | [AuthenticatedUser | null, boolean, (userData: Partial<UserData>) => void]
  | undefined
>(undefined);

/**
 * useAuthenticatedUser returns the user and a loading boolean.
 * Best when used with `<Provider>`.
 *
 * Example usage:
 * ```typescript
 * const [authenticatedUser, loadingAuth] = useAuthenticatedUser();
 * ```
 */
export function useAuthenticatedUser(user?: AuthenticatedUser | null) {
  const context = useContext(AuthContext);
  if (context) return context;
  return _useAuthenticatedUserHook(user || null);
}

export function Provider({
  children,
  authenticatedUser,
}: {
  children: React.ReactNode;
  authenticatedUser: AuthenticatedUser | null;
}) {
  return createElement(
    AuthContext.Provider,
    {value: useAuthenticatedUser(authenticatedUser)},
    children,
  );
}
